import {
  CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH,
  CanonicalInternalComplianceReviewContractError,
  validateCanonicalInternalComplianceDecisionInput,
  type CanonicalInternalComplianceReviewDecision,
  type CanonicalInternalComplianceReviewRequirementKey,
  type CanonicalInternalComplianceReviewState,
} from "@/data/contractor/compliance/canonicalInternalComplianceReview";
import type { CanonicalInternalComplianceReviewClientError } from "@/services/individual-compliance-ops-review.service";
import type { Contractor } from "@/data/types";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CanonicalComplianceOrganizationOption = Readonly<{
  id: string;
  label: string;
}>;

export const CANONICAL_COMPLIANCE_REVIEW_REASONS = Object.freeze([
  Object.freeze({
    code: "document_unreadable",
    label: "Document is unreadable",
  }),
  Object.freeze({
    code: "wrong_document",
    label: "Wrong document submitted",
  }),
  Object.freeze({
    code: "document_expired",
    label: "Document is expired",
  }),
  Object.freeze({
    code: "document_incomplete",
    label: "Document is incomplete",
  }),
  Object.freeze({
    code: "information_mismatch",
    label: "Information does not match",
  }),
  Object.freeze({
    code: "other",
    label: "Other",
  }),
] as const);

export type CanonicalComplianceReviewReasonCode =
  typeof CANONICAL_COMPLIANCE_REVIEW_REASONS[number]["code"];

export const CANONICAL_COMPLIANCE_REQUIREMENT_LABELS: Readonly<
  Record<CanonicalInternalComplianceReviewRequirementKey, string>
> = Object.freeze({
  driver_license: "Driver License",
  license: "License",
  insurance: "Insurance",
  w9: "W-9",
  background_check: "Background Check",
  certification: "Certification",
});

export const CANONICAL_COMPLIANCE_REVIEW_STATE_LABELS: Readonly<
  Record<CanonicalInternalComplianceReviewState, string>
> = Object.freeze({
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
  changes_requested: "Changes requested",
});

export const CANONICAL_COMPLIANCE_DECISION_LABELS: Readonly<
  Record<CanonicalInternalComplianceReviewDecision, string>
> = Object.freeze({
  approved: "Approve",
  rejected: "Reject",
  changes_requested: "Request Changes",
});

export const CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES = Object.freeze({
  authentication_required:
    "Your session has expired. Sign in again to continue.",
  internal_review_forbidden:
    "You do not have permission to review compliance for this organization or contractor.",
  not_found:
    "The requested compliance review is unavailable in the selected scope.",
  stale_decision:
    "Another reviewer changed this decision. Review the latest history before submitting again.",
  idempotency_conflict:
    "This review submission could not be matched safely. Refresh the review before trying again.",
  signing_unavailable:
    "Document preview is temporarily unavailable. Try again later.",
  invalid_request:
    "The review request could not be completed safely.",
  unexpected_error:
    "The compliance review service is temporarily unavailable. Try again.",
  feature_disabled:
    "Canonical Individual Compliance review is not enabled.",
} as const);

export function getCanonicalComplianceReviewErrorMessage(
  error: unknown,
): string {
  if (
    error
    && typeof error === "object"
    && "kind" in error
  ) {
    const kind = String(
      (error as Pick<CanonicalInternalComplianceReviewClientError, "kind">).kind,
    ) as keyof typeof CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES;
    return CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES[kind]
      ?? CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES.unexpected_error;
  }
  return CANONICAL_COMPLIANCE_REVIEW_ERROR_MESSAGES.unexpected_error;
}

export function getCanonicalComplianceReasonLabel(
  reasonCode: string | null,
): string {
  if (!reasonCode) return "No reason";
  return CANONICAL_COMPLIANCE_REVIEW_REASONS.find(
    (reason) => reason.code === reasonCode,
  )?.label ?? reasonCode;
}

export function validateCanonicalComplianceDecisionDraft(args: {
  decision: CanonicalInternalComplianceReviewDecision;
  reasonCode: string;
  note: string;
}): Readonly<{ reasonCode?: string; note?: string }> {
  if (args.reasonCode === "other" && !args.note.trim()) {
    throw new CanonicalInternalComplianceReviewContractError(
      "canonical_internal_compliance_review_other_note_required",
    );
  }
  const normalized = validateCanonicalInternalComplianceDecisionInput({
    decision: args.decision,
    reasonCode:
      args.decision === "approved" ? undefined : args.reasonCode,
    note: args.note,
  });
  return Object.freeze({
    ...(normalized.reasonCode ? { reasonCode: normalized.reasonCode } : {}),
    ...(normalized.note ? { note: normalized.note } : {}),
  });
}

export function remainingCanonicalComplianceNoteCharacters(note: string): number {
  return Math.max(
    0,
    CANONICAL_INTERNAL_COMPLIANCE_NOTE_MAX_LENGTH - note.length,
  );
}

export function buildCanonicalComplianceOrganizationOptions(
  contractors: readonly Contractor[],
): readonly CanonicalComplianceOrganizationOption[] {
  const options = new Map<string, CanonicalComplianceOrganizationOption>();
  for (const contractor of contractors) {
    const organizationId = contractor.organizationId;
    if (!organizationId || !UUID_PATTERN.test(organizationId)) continue;
    if (!options.has(organizationId)) {
      options.set(organizationId, Object.freeze({
        id: organizationId,
        label: `Organization ${organizationId.slice(0, 8)}`,
      }));
    }
  }
  return Object.freeze(
    [...options.values()].sort((a, b) => a.label.localeCompare(b.label)),
  );
}
